/**
 * @fileoverview Network connectivity status tracking and persistence.
 *
 * Tracks online/offline status changes in localStorage for debugging
 * and monitoring network reliability issues.
 *
 * @module helpers/networkStatusStorage
 */

import LocalStorageManager from "@/localStorageManager";
import { jsonParse } from "@/helpers/parser";
import moment from "moment";

/** @type {string} LocalStorage key for network status records */
const storageKey = "networkStatuses";

/**
 * Records a network status change to localStorage.
 *
 * Maintains a rolling history of the last 100 status changes
 * with timestamps for debugging connectivity issues.
 *
 * @param {boolean} isOnline - Current network status (true = online)
 * @returns {Object} The recorded status entry
 * @returns {moment.Moment} returns.t - Timestamp of the status change
 * @returns {boolean} returns.s - The recorded status (online/offline)
 *
 * @example
 * window.addEventListener('online', () => trackNetworkStatus(true));
 * window.addEventListener('offline', () => trackNetworkStatus(false));
 */
export function trackNetworkStatus(isOnline) {
  let records = getTrackedNetworkStatus();
  const record = { t: moment(), s: isOnline };
  records.push(record);
  // Keep last 100 records to prevent unbounded growth
  records = records.slice(-100);
  LocalStorageManager.setItem(storageKey, JSON.stringify(records));

  return record;
}

/**
 * Retrieves all tracked network status records from localStorage.
 *
 * @returns {Array<{t: string, s: boolean}>} Array of status records
 *
 * @example
 * const history = getTrackedNetworkStatus();
 * console.log('Last status:', history[history.length - 1]);
 */
export function getTrackedNetworkStatus() {
  const savedRecords = LocalStorageManager.getItem(storageKey);
  let records: any = [];
  if (savedRecords) {
    records = jsonParse(savedRecords);
  }
  return records;
}

/**
 * Clears all tracked network status records from localStorage.
 *
 * @returns {void}
 */
export function clearTrackedNetworkStatus() {
  LocalStorageManager.removeItem(storageKey);
}
